<template>
  <div class="inventory-barcode-print">
    <a-button icon="printer" @click="open">{{ $t('打印条码') }}</a-button>
    <a-modal
      :visible="visible"
      :title="$t('打印条码')"
      :width="980"
      :maskClosable="false"
      :footer="null"
      @cancel="visible = false"
    >
      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-model :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
            <a-form-model-item v-if="recentPairs.length" :label="$t('历史选择')">
              <div class="recent-tag-wrap">
                <a-tag
                  v-for="item in recentPairs"
                  :key="item.key"
                  closable
                  @close="(e) => handleRemoveRecent(e, item.key)"
                  @click="() => applyRecent(item)"
                >
                  {{ item.brandName }} / {{ item.modelName }}
                </a-tag>
              </div>
            </a-form-model-item>
            <a-form-model-item :label="$t('品牌')">
              <div class="brand-tag-wrap">
                <a-checkable-tag
                  v-for="item in brandOptions"
                  :key="item.value"
                  :checked="form.brandCode === item.value"
                  @change="(checked) => handleBrandTagChange(checked, item.value)"
                >
                  {{ item.label }}
                </a-checkable-tag>
              </div>
            </a-form-model-item>

            <a-form-model-item :label="$t('型号')">
              <a-select
                v-model="form.modelCode"
                show-search
                allow-clear
                :disabled="!form.brandCode"
                :placeholder="$t('请先选品牌')"
                :filterOption="filterOption"
                @change="handleModelChange"
              >
                <a-select-option v-for="item in modelOptions" :key="item.value" :value="item.value">
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </a-form-model-item>

            <a-form-model-item :label="$t('规格')">
              <a-spin :spinning="skuLoading" size="small">
                <div class="spec-wrap" v-if="specFields.length">
                  <div v-for="f in specFields" :key="f.styleType" class="spec-row">
                    <div class="spec-title">{{ f.styleType }}：</div>
                    <div class="spec-values">
                      <a-checkable-tag
                        v-for="v in f.styleValues"
                        :key="`${f.styleType}:${v}`"
                        :checked="form.selectedSpecs[f.styleType] === v"
                        @change="(checked) => handleSpecChange(checked, f.styleType, v)"
                      >
                        {{ v }}
                      </a-checkable-tag>
                    </div>
                  </div>
                </div>
                <span v-else class="text-muted">{{ $t('请先选择型号') }}</span>
              </a-spin>
            </a-form-model-item>

            <a-form-model-item :label="$t('电池')">
              <a-input v-model="form.battery" :placeholder="$t('如：85%')" />
            </a-form-model-item>
            <a-form-model-item :label="$t('价格')">
              <a-input v-model="form.price" />
            </a-form-model-item>
            <a-form-model-item :label="$t('入库日期')">
              <a-date-picker
                style="width: 100%"
                valueFormat="YYYY-MM-DD"
                v-model="form.inboundDate"
                :placeholder="$t('请选择入库日期')"
              />
            </a-form-model-item>
            <a-form-model-item :label="$t('备注')">
              <a-textarea
                v-model="form.remark"
                :rows="2"
                maxlength="26"
                :placeholder="$t('最多26个字')"
              />
            </a-form-model-item>
            <a-form-model-item :label="'IMEI1'">
              <a-input v-model="form.imei1" :placeholder="$t('用于条码展示')" />
            </a-form-model-item>
            <a-form-model-item :label="'IMEI2'">
              <a-input v-model="form.imei2" />
            </a-form-model-item>
            <a-form-model-item :label="'SN'">
              <a-input v-model="form.sn" />
            </a-form-model-item>
          </a-form-model>
        </a-col>

        <a-col :span="12">
          <div class="check-grid">
            <div v-for="item in booleanFields" :key="item.key" class="check-item">
              <div class="flex">
                <div class="check-title mr10">{{ $t(item.label) }}</div>
                <a-radio-group
                  :value="form.checkItems[item.key].status"
                  button-style="solid"
                  size="small"
                  class="status-radio"
                  @change="(e) => handleStatusChange(item.key, e.target.value)"
                >
                  <a-radio-button value="yes">
                    <a-icon type="check" />
                  </a-radio-button>
                  <a-radio-button value="no">
                    <a-icon type="close" />
                  </a-radio-button>
                </a-radio-group>
              </div>
              <a-input
                v-if="form.checkItems[item.key].status === 'no'"
                v-model="form.checkItems[item.key].reason"
                :ref="`reasonInput-${item.key}`"
                :placeholder="$t('请输入打叉原因')"
                style="margin-top: 8px"
              />
            </div>
          </div>

          <div class="preview-card">
            <div class="preview-title">{{ $t('标签预览内容') }}</div>
            <div class="preview-line">{{ previewData.goodsName || '-' }}</div>
            <div class="preview-line">{{ previewData.specText || '-' }}</div>
            <div
              class="preview-line"
              v-for="(line, idx) in previewData.statusLines"
              :key="`status-${idx}`"
            >
              {{ line }}
            </div>
            <div class="preview-line" v-if="previewData.extraText">{{ previewData.extraText }}</div>
            <div class="preview-line" v-if="previewData.inboundText">
              {{ previewData.inboundText }}
            </div>
            <div class="preview-line">IMEI1: {{ form.imei1 || '-' }}</div>
            <div class="preview-line" v-if="form.imei2">IMEI2: {{ form.imei2 }}</div>
            <div class="preview-line" v-if="form.sn">SN: {{ form.sn }}</div>
            <div class="preview-line" v-if="form.remark">{{ $t('备注') }}: {{ form.remark }}</div>
          </div>

          <div class="actions">
            <a-button @click="visible = false">{{ $t('取消') }}</a-button>
            <a-button
              type="primary"
              style="margin-left: 8px"
              :disabled="!previewData.skuCode || !form.imei1"
              @click="print"
            >
              {{ $t('打印') }}
            </a-button>
          </div>
        </a-col>
      </a-row>
    </a-modal>
  </div>
</template>

<script>
import http from '@common/http'
import api from '@common/api'
import { getLodop } from '@/utils/lodopFunc'

const BOOLEAN_FIELDS = [
  { key: 'inWarranty', label: '在保' },
  { key: 'frame', label: '边框' },
  { key: 'screen', label: '屏幕' },
  { key: 'display', label: '屏显' },
  { key: 'faceId', label: '面容' },
  { key: 'function', label: '功能' },
  { key: 'frontCamera', label: '前摄' },
  { key: 'rearCamera', label: '后摄' },
]
const RECENT_CACHE_KEY = 'inventory_barcode_print_recent_pairs_v1'
const RECENT_MAX = 12

function createDefaultCheckItems() {
  return BOOLEAN_FIELDS.reduce((acc, item) => {
    acc[item.key] = { status: 'yes', reason: '' }
    return acc
  }, {})
}

function createDefaultForm() {
  return {
    brandCode: undefined,
    modelCode: undefined,
    selectedSpecs: {},
    skuResult: null,
    battery: '',
    price: '',
    inboundDate: '',
    remark: '',
    imei1: '',
    imei2: '',
    sn: '',
    checkItems: createDefaultCheckItems(),
  }
}

export default {
  name: 'InventoryBarcodePrintModal',
  data() {
    return {
      visible: false,
      skuLoading: false,
      brandOptions: [],
      modelOptions: [],
      recentPairs: [],
      booleanFields: BOOLEAN_FIELDS,
      form: createDefaultForm(),
    }
  },
  computed: {
    specFields() {
      const specOptions = this.form.skuResult?.specOptions || []
      return specOptions.map((item) => ({
        styleType: item.styleType,
        styleValues: Array.isArray(item.styleValues) ? item.styleValues : [],
      }))
    },
    previewData() {
      const relations = this.form.skuResult?.skuRelations || []
      const requiredStyleTypes = this.specFields.map((i) => i.styleType)
      const selected = this.form.selectedSpecs || {}
      const allSelected = requiredStyleTypes.every((k) => !!selected[k])
      const matchedList = relations.filter((r) => {
        const specs = r?.specs || []
        return requiredStyleTypes.every((styleType) => {
          const val = selected?.[styleType]
          if (!val) return false
          return specs.some((s) => s.styleType === styleType && s.styleValue === val)
        })
      })
      const matched = allSelected && matchedList.length === 1 ? matchedList[0] : null
      const checkTexts = this.booleanFields
        .map((item) => {
          const row = this.form.checkItems[item.key]
          if (!row) return ''
          if (row.status === 'yes') return `${this.$t(item.label)}✓`
          return row.reason ? `${this.$t(item.label)}:${row.reason}` : `${this.$t(item.label)}:✗`
        })
        .filter(Boolean)
      const extraTexts = [
        this.form.battery ? `${this.$t('电池')}:${this.form.battery}%` : '',
        this.form.price ? `${this.$t('价格')}:${this.form.price}` : '',
      ].filter(Boolean)
      const specOrder = ['颜色', '运行内存', '机身存储', '成新', '版本']
      const selectedSpecs = selected || {}
      const orderedSpecs = [
        ...specOrder.map((key) => selectedSpecs[key]).filter(Boolean),
        ...Object.keys(selectedSpecs)
          .filter((key) => !specOrder.includes(key))
          .map((key) => selectedSpecs[key])
          .filter(Boolean),
      ]
      const statusLines = []
      if (checkTexts.length) {
        statusLines.push(checkTexts.slice(0, 3).join('  '))
        if (checkTexts.length > 3) statusLines.push(checkTexts.slice(3, 6).join('  '))
        if (checkTexts.length > 6) statusLines.push(checkTexts.slice(6, 8).join('  '))
      }
      return {
        goodsName: this.form.skuResult?.goodsName || '',
        specText: orderedSpecs.join(' | '),
        statusText: checkTexts.join('  '),
        statusLines,
        extraText: extraTexts.join('  '),
        inboundText: this.form.inboundDate ? `${this.$t('入库')}:${this.form.inboundDate}` : '',
        skuCode: matched?.skuCode || '',
      }
    },
  },
  methods: {
    filterOption(input, option) {
      return (option.componentOptions?.children?.[0]?.text || '')
        .toLowerCase()
        .includes(input.toLowerCase())
    },
    parseBrandListToOptions(brandList) {
      return (brandList || []).map((b) => ({
        label: b.brandName,
        value: b.brandId,
        raw: b,
      }))
    },
    resetModalForm() {
      this.form = createDefaultForm()
      this.modelOptions = []
    },
    open() {
      this.visible = true
      this.resetModalForm()
      this.skuLoading = false
      this.loadRecentPairs()
      this.fetchBrandList()
    },
    fetchBrandList() {
      return new Promise((resolve) => {
        http({
          type: 'post',
          url: api.getUPDBrandList,
          data: {},
          hasLoading: false,
          success: (res) => {
            this.brandOptions = this.parseBrandListToOptions(res?.result?.brandList || [])
            resolve()
          },
          fail: () => resolve(),
        })
      })
    },
    handleBrandChange(brandCode) {
      this.form.modelCode = undefined
      this.form.selectedSpecs = {}
      this.form.skuResult = null
      const brandRaw = this.brandOptions.find((b) => b.value === brandCode)?.raw
      this.modelOptions = (brandRaw?.brandModelList || []).map((m) => ({
        label: m.modelName,
        value: m.modelCode,
      }))
    },
    handleBrandTagChange(checked, brandCode) {
      const nextBrandCode = checked ? brandCode : undefined
      this.form.brandCode = nextBrandCode
      this.handleBrandChange(nextBrandCode)
    },
    handleModelChange(modelCode) {
      this.form.selectedSpecs = {}
      this.form.skuResult = null
      if (!modelCode) return
      const model = this.modelOptions.find((item) => item.value === modelCode)
      if (!model?.label) return
      this.querySkuRelationMap(modelCode, model.label)
    },
    loadRecentPairs() {
      try {
        const raw = localStorage.getItem(RECENT_CACHE_KEY)
        const list = raw ? JSON.parse(raw) : []
        this.recentPairs = Array.isArray(list) ? list : []
      } catch (e) {
        this.recentPairs = []
      }
    },
    persistRecentPairs() {
      try {
        localStorage.setItem(RECENT_CACHE_KEY, JSON.stringify(this.recentPairs || []))
      } catch (e) {
        // ignore
      }
    },
    saveRecentPair() {
      if (!this.form.brandCode || !this.form.modelCode) return
      const brandName = this.brandOptions.find((b) => b.value === this.form.brandCode)?.label || ''
      const modelName = this.modelOptions.find((m) => m.value === this.form.modelCode)?.label || ''
      const key = `${this.form.brandCode}__${this.form.modelCode}`
      const next = [
        {
          key,
          brandCode: this.form.brandCode,
          brandName,
          modelCode: this.form.modelCode,
          modelName,
          ts: Date.now(),
        },
        ...(this.recentPairs || []).filter((x) => x && x.key !== key),
      ].slice(0, RECENT_MAX)
      this.recentPairs = next
      this.persistRecentPairs()
    },
    handleRemoveRecent(e, key) {
      try {
        e && e.preventDefault && e.preventDefault()
        e && e.stopPropagation && e.stopPropagation()
      } catch (err) {
        // ignore
      }
      this.recentPairs = (this.recentPairs || []).filter((x) => x && x.key !== key)
      this.persistRecentPairs()
    },
    applyRecent(item) {
      if (!item?.brandCode || !item?.modelCode) return
      const apply = () => {
        this.form.brandCode = item.brandCode
        this.handleBrandChange(item.brandCode)
        this.$nextTick(() => {
          this.form.modelCode = item.modelCode
          this.handleModelChange(item.modelCode)
        })
      }
      if (this.brandOptions.length) {
        apply()
      } else {
        this.fetchBrandList().then(() => apply())
      }
    },
    querySkuRelationMap(modelCode, modelName) {
      this.skuLoading = true
      http({
        type: 'post',
        url: api.querySkuRelationMap,
        data: { modelCode, modelName },
        hasLoading: false,
        success: (res) => {
          this.form.skuResult = res?.result || null
          this.autoSelectSingleSpecValues()
          this.skuLoading = false
        },
        fail: () => {
          this.skuLoading = false
        },
      })
    },
    autoSelectSingleSpecValues() {
      const next = { ...(this.form.selectedSpecs || {}) }
      this.specFields.forEach((field) => {
        const values = field.styleValues || []
        if (values.length === 1) next[field.styleType] = values[0]
      })
      this.form.selectedSpecs = next
    },
    handleSpecChange(checked, styleType, value) {
      const next = { ...(this.form.selectedSpecs || {}) }
      next[styleType] = checked ? value : undefined
      this.form.selectedSpecs = next
    },
    handleStatusChange(key, status) {
      const next = { ...(this.form.checkItems || {}) }
      next[key] = {
        ...(next[key] || { reason: '' }),
        status,
        reason: status === 'yes' ? '' : next[key]?.reason || '',
      }
      this.form.checkItems = next
      if (status === 'no') {
        this.$nextTick(() => {
          const ref = this.$refs[`reasonInput-${key}`]
          const target = Array.isArray(ref) ? ref[0] : ref
          if (target?.focus) {
            target.focus()
            return
          }
          const inputEl = target?.$el?.querySelector('input') || target?.input
          if (inputEl?.focus) {
            inputEl.focus()
          }
        })
      }
    },
    escapeHtml(text = '') {
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    buildPrintHtml() {
      const imei2 = this.form.imei2 || ''
      const sn = this.form.sn || ''
      const remark = this.form.remark || ''
      return `
        <div style="font-size:10px;line-height:1.3;font-family:Arial;padding:0 1mm;word-break:break-all;">
          <div style="font-size:12px;font-weight:700;max-height:30px;overflow:hidden;">${this.escapeHtml(
            this.previewData.goodsName || ''
          )}</div>
          <div style="margin-top:1px;">${this.escapeHtml(this.previewData.specText || '')}</div>
          ${(this.previewData.statusLines || [])
            .map((line) => `<div style="margin-top:1px;">${this.escapeHtml(line)}</div>`)
            .join('')}
          ${
            this.previewData.extraText
              ? `<div style="margin-top:1px;">${this.escapeHtml(this.previewData.extraText)}</div>`
              : ''
          }
          ${
            this.previewData.inboundText
              ? `<div style="margin-top:1px;">${this.escapeHtml(
                  this.previewData.inboundText
                )}</div>`
              : ''
          }
          <div style="margin-top:2px;">IMEI1: ${this.escapeHtml(this.form.imei1 || '')}</div>
          ${imei2 ? `<div>IMEI2: ${this.escapeHtml(imei2)}</div>` : ''}
          ${sn ? `<div>SN: ${this.escapeHtml(sn)}</div>` : ''}
          ${remark ? `<div>${this.escapeHtml(remark)}</div>` : ''}
        </div>
      `
    },
    print() {
      if (!this.previewData.skuCode) {
        this.$message.warning(this.$t('请先选择完整规格，确保唯一SKU'))
        return
      }
      if (!this.form.imei1) {
        this.$message.warning(this.$t('请先输入IMEI1'))
        return
      }
      const LODOP = getLodop()
      if (!LODOP) {
        this.$message.error(this.$t('未检测到打印控件，请检查Lodop服务'))
        return
      }
      this.saveRecentPair()
      LODOP.PRINT_INIT('inventory_barcode_print')
      // 40mm(宽) * 80mm(高)
      LODOP.SET_PRINT_PAGESIZE(1, 400, 700, '')
      const skuCode = (this.previewData.skuCode || '').trim()
      const skuLen = skuCode.length
      const skuBarcodeWidth =
        skuLen <= 14 ? '35.5mm' : skuLen <= 18 ? '34mm' : skuLen <= 22 ? '32mm' : '30mm'
      const skuBarcodeLeft =
        skuLen <= 14 ? '2.25mm' : skuLen <= 18 ? '3mm' : skuLen <= 22 ? '4mm' : '5mm'
      // 顶部先打 SKU 条码
      LODOP.ADD_PRINT_BARCODE('1mm', skuBarcodeLeft, skuBarcodeWidth, '8mm', '128A', skuCode)
      LODOP.SET_PRINT_STYLEA(0, 'ShowBarText', 0)
      // 文本整体下移，避免顶部拥挤
      LODOP.ADD_PRINT_HTM('10.5mm', '0', '40mm', '56mm', this.buildPrintHtml())
      const imeiLen = (this.form.imei1 || '').trim().length
      const barcodeWidth = imeiLen === 14 ? '35.5mm' : '34mm'
      const barcodeLeft = imeiLen === 14 ? '2.25mm' : '3mm'
      // IMEI1 条码放在底部，跟在 IMEI1 文本段之后
      LODOP.ADD_PRINT_TEXT('53mm', '10mm', '10mm', '3.5mm', 'IMEI1')
      LODOP.SET_PRINT_STYLEA(0, 'FontSize', 7)
      LODOP.ADD_PRINT_BARCODE('56mm', barcodeLeft, barcodeWidth, '10mm', '128A', this.form.imei1)
      LODOP.SET_PRINT_STYLEA(0, 'ShowBarText', 1)
      LODOP.SET_PRINT_STYLEA(0, 'Bold', 0)
      LODOP.PRINT()
      this.resetModalForm()
    },
  },
}
</script>

<style scoped lang="scss">
.text-muted {
  color: rgba(0, 0, 0, 0.45);
}
.brand-tag-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 132px;
  overflow: auto;
  padding-top: 4px;
}
.recent-tag-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.spec-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.spec-row {
  display: flex;
}
.spec-title {
  min-width: 72px;
  color: rgba(0, 0, 0, 0.65);
}
.spec-values {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.check-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
.check-item {
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 8px;
}
.check-title {
  margin-bottom: 6px;
  font-weight: 600;
}
.status-radio :deep(.ant-radio-button-wrapper) {
  min-width: 40px;
  text-align: center;
  padding: 0 10px;
}
.preview-card {
  margin-top: 12px;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  padding: 10px;
}
.preview-title {
  font-weight: 600;
  margin-bottom: 8px;
}
.preview-line {
  line-height: 1.6;
  word-break: break-all;
}
.actions {
  text-align: right;
  margin-top: 16px;
}
:deep(.ant-form-item) {
  margin-bottom: 6px;
}
:deep(.ant-form-item-control) {
  line-height: normal;
  margin-top: 4px;
}
:deep(.spec-row) {
  margin-top: 10px;
  display: flex;
  align-items: center;
}
:deep(.ant-tag) {
  cursor: pointer;
}
</style>
